import { useEffect, useMemo, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const COLS = 8;
const ROWS = 6;
const TOTAL = COLS * ROWS;
const DOTS = 42;
const RACK_ROWS = 5;
const RACK_COLS = 12;

const hash = (n) => {
  const x = Math.sin(n * 127.1) * 43758.5453;
  return x - Math.floor(x);
};

const IMG_PORT =
  "https://images.pexels.com/photos/20216716/pexels-photo-20216716.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function DigitalTransition() {
  const root = useRef(null);

  const cells = useMemo(
    () =>
      Array.from({ length: TOTAL }, (_, i) => ({
        key: i,
        fill: hash(i) > 0.82 ? "amber" : hash(i) > 0.55 ? "graphite" : "empty",
      })),
    []
  );

  const dots = useMemo(
    () =>
      Array.from({ length: DOTS }, (_, i) => ({
        key: i,
        x: 6 + hash(i * 3) * 88,
        y: 12 + hash(i * 3 + 1) * 72,
        depth: 0.4 + hash(i * 3 + 2) * 0.6,
        amber: hash(i * 5 + 2) > 0.86,
      })),
    []
  );

  const lines = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) => {
        const a = dots[i % DOTS];
        const b = dots[(i * 7 + 5) % DOTS];
        return { key: i, x1: a.x, y1: a.y, x2: b.x, y2: b.y };
      }),
    [dots]
  );

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "+=1000%",
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
      });

      /* isolate the container grid from the photography */
      tl.fromTo(".dt-overline", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.3 }, 0);
      tl.fromTo(
        ".dt-photo",
        { filter: "grayscale(0%) brightness(1)" },
        { filter: "grayscale(75%) brightness(0.55)", duration: 0.8, ease: "power1.inOut" },
        0.1
      );
      tl.to(".dt-cell", { borderColor: "rgba(255,255,255,0.45)", duration: 0.6 }, 0.5);
      tl.to(
        ".dt-cell",
        {
          backgroundColor: (i) => (cells[i].fill === "amber" ? "#FF5A00" : cells[i].fill === "graphite" ? "#1b1b1e" : "#101012"),
          duration: 0.7,
          stagger: { each: 0.006, from: "random" },
        },
        1.3
      );
      tl.to(".dt-photo", { opacity: 0.1, duration: 0.6 }, 1.5);

      /* blocks become pixels, pixels enter data space */
      tl.to(".dt-cell", { scale: 0.22, duration: 0.6, ease: "power2.inOut", stagger: { each: 0.004, from: "center" } }, 2.2);
      tl.to(".dt-gridwrap", { scale: 1.4, opacity: 0, duration: 0.7, ease: "power2.in" }, 2.6);
      tl.fromTo(
        ".data-dot",
        { opacity: 0, scale: 0.2 },
        { opacity: (i) => 0.3 + dots[i].depth * 0.6, scale: 1, duration: 0.6, ease: "power2.out", stagger: { each: 0.012, from: "random" } },
        2.7
      );
      tl.fromTo(
        ".net-line",
        { strokeDashoffset: 1 },
        { strokeDashoffset: 0, duration: 0.8, ease: "power1.inOut", stagger: 0.05 },
        3.2
      );

      /* headline — synchronized with the transformation */
      tl.fromTo(".hh-line-1", { y: 0, yPercent: 120 }, { y: 0, yPercent: 0, duration: 0.45, ease: "power3.out" }, 3.6);
      tl.fromTo(".hh-line-2", { y: 0, yPercent: 120 }, { y: 0, yPercent: 0, duration: 0.45, ease: "power3.out" }, 3.8);
      /* visual pause */
      tl.to({}, { duration: 0.7 });
      tl.fromTo(".hh-line-3", { y: 0, yPercent: 120 }, { y: 0, yPercent: 0, duration: 0.45, ease: "power3.out" }, 5.0);
      tl.fromTo(".hh-line-4", { y: 0, yPercent: 120 }, { y: 0, yPercent: 0, duration: 0.45, ease: "power3.out" }, 5.25);
      tl.to(".data-space", { scale: 1.1, duration: 2, ease: "none" }, 3.4);

      /* cells reorganize into ordered infrastructure */
      tl.to(".hh-line", { opacity: 0.28, duration: 0.5, ease: "power1.inOut" }, 6.1);
      tl.to(".data-dot", { opacity: 0.18, duration: 0.6 }, 6.0);
      tl.to(".net-line", { opacity: 0.25, duration: 0.6 }, 6.0);
      tl.fromTo(
        ".rack-row",
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.1, ease: "power2.out" },
        6.2
      );

      /* technology emerges from the infrastructure */
      tl.fromTo(".cap-1", { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.45, ease: "power3.out" }, 6.9);
      tl.to(".cap-1", { opacity: 0, y: -40, duration: 0.4, ease: "power2.in" }, 7.6);
      tl.fromTo(".cap-2", { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.45, ease: "power3.out" }, 7.8);
      tl.to(".dt-overline", { opacity: 0, duration: 0.3 }, 8.0);
      tl.to({}, { duration: 0.5 });
    }, root);
    return () => ctx.revert();
  }, [cells, dots]);

  return (
    <section
      ref={root}
      id="transition"
      className="relative h-[100svh] overflow-hidden"
      data-testid="digital-transition-scene"
      aria-label="From physical logistics to digital infrastructure"
    >
      <img
        src={IMG_PORT}
        alt="Container terminal at night"
        className="dt-photo absolute inset-0 h-full w-full object-cover"
        loading="lazy"
        decoding="async"
      />

      <p className="dt-overline absolute left-[6vw] top-[14vh] z-30 font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs">
        04 — Physical → Digital
      </p>

      {/* data space: dots + network lines */}
      <div className="data-space pointer-events-none absolute inset-0 z-10" aria-hidden="true">
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {lines.map((l) => (
            <line
              key={l.key}
              className="net-line"
              x1={l.x1}
              y1={l.y1}
              x2={l.x2}
              y2={l.y2}
              stroke="rgba(245,245,245,0.22)"
              strokeWidth="0.12"
              pathLength={1}
              strokeDasharray={1}
              strokeDashoffset={1}
            />
          ))}
        </svg>
        {dots.map((d) => (
          <span
            key={d.key}
            className="data-dot absolute rounded-full"
            style={{
              left: `${d.x}%`,
              top: `${d.y}%`,
              width: `${3 + d.depth * 5}px`,
              height: `${3 + d.depth * 5}px`,
              background: d.amber ? "#FF5A00" : "#F5F5F5",
              opacity: 0,
            }}
          />
        ))}
      </div>

      {/* container grid */}
      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
        <div className="dt-gridwrap grid w-[76vmin] grid-cols-8 gap-[3px]" data-testid="container-grid">
          {cells.map((c) => (
            <div
              key={c.key}
              className={`dt-cell aspect-[4/3] border ${
                c.fill === "amber" ? "border-amber/40" : "border-white/15"
              } bg-transparent`}
            />
          ))}
        </div>
      </div>

      {/* headline */}
      <div className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center px-6 text-center">
        <div className="overflow-hidden">
          <h2 className="hh-line hh-line-1 font-display text-[8vw] font-black uppercase leading-[1] tracking-tighter md:text-[5vw]">
            We don&rsquo;t just
          </h2>
        </div>
        <div className="overflow-hidden">
          <h2 className="hh-line hh-line-2 font-display text-[8vw] font-black uppercase leading-[1] tracking-tighter md:text-[5vw]">
            move goods.
          </h2>
        </div>
        <div className="mt-6 overflow-hidden md:mt-10">
          <h2 className="hh-line hh-line-3 font-display text-[10vw] font-black uppercase leading-[1] tracking-tighter md:text-[7vw]">
            We move
          </h2>
        </div>
        <div className="overflow-hidden">
          <h2
            className="hh-line hh-line-4 font-display text-[10vw] font-black uppercase leading-[1] tracking-tighter text-amber md:text-[7vw]"
            data-testid="digital-transition-headline"
          >
            information.
          </h2>
        </div>
      </div>

      {/* ordered infrastructure rack */}
      <div className="pointer-events-none absolute bottom-[7vh] left-1/2 z-20 w-[72vw] -translate-x-1/2" aria-hidden="true">
        {Array.from({ length: RACK_ROWS }, (_, r) => (
          <div key={r} className="rack-row mb-2 flex gap-2">
            {Array.from({ length: RACK_COLS }, (_, c) => (
              <span
                key={c}
                className={`h-2.5 flex-1 md:h-3 ${hash(r * 31 + c * 7) > 0.88 ? "bg-amber/70" : "bg-white/10"}`}
              />
            ))}
          </div>
        ))}
      </div>

      {/* emerging technology captions */}
      <div className="cap-1 pointer-events-none absolute bottom-[26vh] left-[6vw] z-30">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber md:text-xs">From the same infrastructure</p>
        <h3 className="mt-3 font-display text-[7vw] font-black uppercase leading-[0.9] tracking-tighter md:text-5xl" data-testid="cap-digital-transformation">
          Digital Transformation
          <br />
          &amp; Technology Consulting
        </h3>
      </div>
      <div className="cap-2 pointer-events-none absolute bottom-[26vh] left-[6vw] z-30">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber md:text-xs">From the same infrastructure</p>
        <h3 className="mt-3 font-display text-[7vw] font-black uppercase leading-[0.9] tracking-tighter md:text-5xl" data-testid="cap-custom-software">
          Custom Software
          <br />
          Development
        </h3>
      </div>
    </section>
  );
}
